/**
 * Sanerar HTML innan den sätts med dangerouslySetInnerHTML.
 *
 * Spotlight-artiklarnas kropp renderas med den funktionen på en publik sida.
 * Kommentaren i ``markdown.js`` påstod att markdown-renderaren körde med
 * "safe defaults (no raw HTML through)". Det stämde inte: ``marked`` släpper
 * igenom rå HTML som standard, och saneringsflaggan togs bort ur biblioteket i
 * version 5. Ett ``<script>`` i artikeltexten kördes alltså i besökarens
 * webbläsare på talentsio.com.
 *
 * Servern sanerar redan vid sparandet, i ``markdown_policy.py``. Den här filen
 * är andra lagret, för innehåll som redan ligger i databasen och för varje
 * framtida yta som råkar rendera samma text. Två lager är inte överflödigt när
 * det ena är en regex på markdown och det andra läser den HTML som faktiskt
 * hamnar i sidan.
 *
 * Fyra regler:
 *
 *  1. **Tillåtlista, aldrig blocklista.** Ett element som inte står i listan
 *     tas bort. En blocklista är en gissning om vad angriparen tänker på.
 *  2. **Inga attribut utom de uppräknade.** Det tar bort hela ``on*``-familjen,
 *     ``style``, ``srcdoc`` och allt annat på en gång, för alltid.
 *  3. **Adresser kontrolleras separat.** Ett ``<a>`` är ofarligt tills dess
 *     ``href`` är ``javascript:``.
 *  4. **Texten bevaras när elementet tas bort.** En artikel ska inte tappa
 *     stycken för att någon skrivit en tagg. Undantaget är element vars
 *     innehåll är kod och inte text.
 */

// Se regel 1. Det som behövs för en artikel, och ingenting mer.
const ALLOWED_TAGS = new Set([
  "P", "BR", "HR", "STRONG", "B", "EM", "I", "U", "S", "DEL", "INS", "MARK",
  "A", "UL", "OL", "LI", "BLOCKQUOTE", "CODE", "PRE", "IMG",
  "H1", "H2", "H3", "H4", "H5", "H6",
  "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TH", "TD",
  "FIGURE", "FIGCAPTION", "SPAN", "DIV",
]);

// Se regel 2. Per element, aldrig globalt.
const ALLOWED_ATTRS = {
  A: ["href", "title"],
  IMG: ["src", "alt", "title", "width", "height", "loading"],
  TH: ["colspan", "rowspan"],
  TD: ["colspan", "rowspan"],
  OL: ["start"],
};

// Element vars innehåll är kod. Se regel 4: de tas bort med sitt innehåll.
const DROP_WITH_CONTENT = new Set([
  "SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "SVG", "MATH", "TEMPLATE",
  "NOSCRIPT", "FRAME", "FRAMESET", "BASE", "LINK", "META", "FORM", "INPUT",
  "BUTTON", "SELECT", "TEXTAREA",
]);

const BLOCKED_SCHEMES = ["javascript:", "data:", "vbscript:", "blob:", "file:", "about:"];

/** Se regel 3. Tom sträng betyder att attributet ska bort helt. */
export const safeHref = (value) => {
  const raw = String(value == null ? "" : value).trim();
  if (!raw) return "";
  // Kontrollen görs på en variant utan blanksteg och kontrolltecken, eftersom
  // "java\tscript:" är samma sak för webbläsaren men inte för en enkel jämförelse.
  const flat = raw.replace(/[\s -]/g, "").toLowerCase();
  if (BLOCKED_SCHEMES.some((s) => flat.startsWith(s))) return "";
  // Schemalösa adresser som //angriparen.example ärver sidans schema.
  if (flat.startsWith("//")) return "";
  return raw;
};

/**
 * HTML in, säker HTML ut.
 *
 * Sanerar med webbläsarens egen parser i stället för med ett mönster. En regex
 * mot HTML läser inte samma dokument som webbläsaren gör, och skillnaden mellan
 * de två är precis där en förbigång bor.
 */
export function sanitizeHtml(html) {
  const input = String(html == null ? "" : html);
  if (!input) return "";
  if (typeof DOMParser === "undefined") {
    // Utan en parser, till exempel vid serverrendering, är det enda säkra
    // svaret ingen HTML alls. Att returnera indata orörd vore att stänga av
    // saneringen i just den miljö vi inte kan se.
    return "";
  }

  const doc = new DOMParser().parseFromString(`<body>${input}</body>`, "text/html");
  const walk = (node) => {
    // Bakifrån, eftersom listan ändras medan vi går igenom den.
    for (let i = node.childNodes.length - 1; i >= 0; i -= 1) {
      const child = node.childNodes[i];
      if (child.nodeType === 3 || child.nodeType === 4) continue;   // text och CDATA
      if (child.nodeType === 8) { child.remove(); continue; }       // kommentar
      if (child.nodeType !== 1) { child.remove(); continue; }

      const tag = child.tagName ? child.tagName.toUpperCase() : "";

      if (DROP_WITH_CONTENT.has(tag)) { child.remove(); continue; }

      if (!ALLOWED_TAGS.has(tag)) {
        // Se regel 4. Elementet bort, barnen kvar.
        walk(child);
        while (child.firstChild) node.insertBefore(child.firstChild, child);
        child.remove();
        continue;
      }

      const allowed = ALLOWED_ATTRS[tag] || [];
      for (let a = child.attributes.length - 1; a >= 0; a -= 1) {
        const attr = child.attributes[a];
        const name = attr.name.toLowerCase();
        if (!allowed.includes(name)) { child.removeAttribute(attr.name); continue; }
        if (name === "href" || name === "src") {
          const safe = safeHref(attr.value);
          if (!safe) child.removeAttribute(attr.name);
          else child.setAttribute(name, safe);
        }
      }
      // En länk som öppnas i en ny flik utan rel kan nå vår sida via
      // window.opener. Vi sätter det i stället för att lita på författaren.
      if (tag === "A" && child.getAttribute("href")) {
        child.setAttribute("rel", "noreferrer noopener");
        child.setAttribute("target", "_blank");
      }
      walk(child);
    }
  };

  walk(doc.body);
  return doc.body.innerHTML;
}

export default sanitizeHtml;
